// 1 Hz Group-1 field playback for KPI tiles — F1 implementation.
// Spec: docs/superpowers/specs/2026-06-30-cloud-kpi-tiles-1hz-playback-f1-design.md
// Issue: djin-cloud#552
//
// Subscribes to `telemetry:batch`, enqueues the per-second `samples[]` that
// genieos already emits on every batch, and dequeues one entry per 1 Hz
// PlaybackClock tick. Returns a Group-1 projection of the latest dequeued
// sample. On queue underrun, holds the last real sample — never interpolates
// or fabricates.
//
// Designed to be merged with useDelayedSiteSummary in ProjectSummary:
//   const latestSiteSummary = { ...group2Summary, ...group1Playback }
// Group-1 keys win because they are spread last; Group-2 fields continue to
// arrive at the 5 s batch cadence from useDelayedSiteSummary.

import { useEffect, useRef, useState } from 'react';
import { socketService } from '../services/socket';
import { usePlaybackClock } from './usePlaybackClock';

// ---------------------------------------------------------------------------
// Group-1 field definition
// ---------------------------------------------------------------------------

/**
 * Site-level fields that are present on each per-second genieos sample (in
 * ALLOWED_SAMPLE_FIELDS on the backend) AND directly bound by a KPI tile or
 * gauge in this UI.  These are played back at 1 Hz; everything else stays at
 * the ~5 s batch cadence (Group-2).
 *
 * Derivation (F1 spec §"Field grouping"):
 *   Condition (a) — a tile/gauge actually binds the field through kpiValues:
 *     PersistentKPIBar   → total_load_kw, capacity_online_kw, bess_available_kw,
 *                          bess_available_kwh
 *     SystemSummaryCard  → total_load_kw, total_reactive_power_kvar,
 *                          total_apparent_power_kva, capacity_online_kw,
 *                          grid_frequency_hz
 *     BatteryStorageCard → bess_available_kwh
 *     GeneratorsCard     → fuel_consumption_mmbtu_h
 *     SiteMeterCard      → grid_frequency_hz
 *   Condition (b) — field is present on each per-second sample (in
 *     ALLOWED_SAMPLE_FIELDS in backend/src/services/event-hub-consumer.service.ts).
 *
 * F2a additions (djin-cloud#552) — fields now derived per-second by the backend
 * and present on every sample, so promoted to Group-1 for 1 Hz playback:
 *   site_power_factor, capacity_total_kw, site_utilization_pct,
 *   bess_total_count, generators_total_count
 *
 * Excluded → Group-2 (device-dependent or aggregated, stay at 5 s cadence):
 *   bess_online_count,
 *   meter_power_factor, meter_voltage_a_v, meter_voltage_b_v, meter_voltage_c_v,
 *   meter_active_power_kw, generators_online_count, generators[]
 *
 * Note: field names are identical between the genieos sample wire format and
 * the site_summary keys the tiles read (no mapping needed for Group-1).
 */
export const SITE_SUMMARY_GROUP1_KEYS = [
  'total_load_kw',
  'total_reactive_power_kvar',
  'total_apparent_power_kva',
  'capacity_online_kw',
  'bess_available_kwh',
  'bess_available_kw',
  'fuel_consumption_mmbtu_h',
  'grid_frequency_hz',
  // F2a: site-derivable fields now computed per-second by the backend (djin-cloud#552)
  'site_power_factor',
  'capacity_total_kw',
  'site_utilization_pct',
  'bess_total_count',
  'generators_total_count',
] as const;

export type Group1Key = (typeof SITE_SUMMARY_GROUP1_KEYS)[number];

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

/** FIFO queue cap — prevents unbounded growth under a delivery burst. */
const QUEUE_CAP = 60;

// ---------------------------------------------------------------------------
// Pure helper (exported for testing and for ProjectSummary merge)
// ---------------------------------------------------------------------------

/**
 * Project only Group-1 keys from a raw per-second sample.
 * Keys absent on the sample are left absent in the result — no fabrication.
 */
export function projectGroup1(
  sample: Record<string, unknown>,
): Partial<Record<Group1Key, unknown>> {
  const out: Partial<Record<Group1Key, unknown>> = {};
  for (const key of SITE_SUMMARY_GROUP1_KEYS) {
    if (Object.prototype.hasOwnProperty.call(sample, key)) {
      out[key] = sample[key];
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface TelemetryBatchData {
  site_id: string;
  samples?: unknown[];
}

/**
 * useSiteSummaryPlayback — drives Group-1 KPI tile fields at 1 Hz.
 *
 * Subscribes to `telemetry:batch` WebSocket events, enqueues the per-second
 * `samples[]` (already sanitized/present on every batch from genieos), and
 * dequeues one sample per PlaybackClock tick (~1 s). Returns a Group-1
 * projection of the most recently dequeued sample.
 *
 * Underrun (empty queue between batches): holds the last real sample value.
 * Burst (>QUEUE_CAP samples backlog): oldest entries are dropped to cap queue.
 * Site change: queue and displayed state reset synchronously.
 *
 * Wire into ProjectSummary as:
 *   const group1Playback = useSiteSummaryPlayback(id);
 *   const group2Summary  = useDelayedSiteSummary(id);
 *   const latestSiteSummary = { ...group2Summary, ...group1Playback };
 * Group-1 wins because it is spread last.
 */
export function useSiteSummaryPlayback(
  siteId: string | undefined,
): Record<string, unknown> {
  // FIFO queue — mutable ref, never triggers re-renders on enqueue.
  const queueRef = useRef<Record<string, unknown>[]>([]);
  const [latest, setLatest] = useState<Record<string, unknown>>({});

  // --- Socket subscription ---------------------------------------------------
  // Reset + re-subscribe whenever siteId changes.
  useEffect(() => {
    queueRef.current = [];
    setLatest({});

    const socket = socketService.getSocket();
    if (!socket || !siteId) return;

    const handler = (data: unknown) => {
      if (!data || typeof data !== 'object') return;
      const batch = data as TelemetryBatchData;
      if (batch.site_id !== siteId) return;
      if (!Array.isArray(batch.samples) || batch.samples.length === 0) return;

      for (const raw of batch.samples) {
        if (raw && typeof raw === 'object') {
          queueRef.current.push(raw as Record<string, unknown>);
        }
      }
      // Cap: drop oldest when queue grows beyond QUEUE_CAP.
      if (queueRef.current.length > QUEUE_CAP) {
        queueRef.current.splice(0, queueRef.current.length - QUEUE_CAP);
      }
    };

    socket.on('telemetry:batch', handler);
    return () => {
      socket.off('telemetry:batch', handler);
    };
  }, [siteId]);

  // --- 1 Hz tick -----------------------------------------------------------
  // Dequeue one sample per clock tick; hold last on underrun.
  const now = usePlaybackClock(!!siteId);
  useEffect(() => {
    if (!siteId) return;
    const sample = queueRef.current.shift();
    if (sample === undefined) return; // underrun — hold last, no state change
    setLatest(projectGroup1(sample));
  }, [now, siteId]);

  return latest;
}
